import styled from 'styled-components';
import { AiOutlineClose } from 'react-icons/ai';
import { RiMenu4Fill } from 'react-icons/ri';
import { Link, useLocation } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import logo from "../../../assets/images/conference_logo.jpg" 


const IUMRSNavBar = ({ navroute }) => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [showMobileNav, setShowMobileNav] = useState(false);
  const navRef = useRef(null);
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const location = useLocation();
  const currentPath = location.pathname;
  

  const MOBILE_BREAKPOINT = 880;
  
  const toggleDropdown = (index) => {
    if (window.innerWidth <= MOBILE_BREAKPOINT) {
      setActiveDropdown(activeDropdown === index ? null : index);
    }
  };

  const toggleMobileNav = () => {
    setShowMobileNav(!showMobileNav);
  };

  const isActive = (navLink) => {
    return currentPath === navLink;
  };

  const isActiveOrContainsActive = (navLink, options) => {
    if (currentPath === navLink) return true;
    
    if (options && currentPath.startsWith(`${navLink}/`)) {
      return !options.some(option => currentPath === option.link);
    }
    
    return false;
  };

  const isDropdownActive = (options) => {
    if (!options) return false;
    return options.some(option => currentPath === option.link);
  };

  const handleMouseEnter = (index) => {
    if (window.innerWidth > MOBILE_BREAKPOINT) {
      setActiveDropdown(index);
    }
  };

  const handleMouseLeave = () => {
    if (window.innerWidth > MOBILE_BREAKPOINT) {
      setActiveDropdown(null);
    }
  };

  useEffect(() => {
    const updateHeaderHeight = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
    };

    updateHeaderHeight();

    window.addEventListener('resize', updateHeaderHeight);
    
    return () => {
      window.removeEventListener('resize', updateHeaderHeight);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (window.innerWidth <= MOBILE_BREAKPOINT && navRef.current && !navRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <Header ref={headerRef}>
      <NavContainer>
        <TopBar>
          
          <LogoWrapper as="a" href="/iumrs-icam25">
            <LogoContent>
              <h3>IUMRS-ICAM25</h3>
              {logo && <img src={logo} alt="Event Logo" />}
            </LogoContent>
          </LogoWrapper>
          <NavWrapper 
            ref={navRef} 
            className={showMobileNav ? 'mobile-active' : ''}
            style={{ '--header-height': `${headerHeight}px` }}
          >
            <NavList>
              {navroute.map((navItem, index) => (
                <NavItem
                  key={navItem.typeId}
                  className={
                    (isActiveOrContainsActive(navItem.link, navItem.options) || 
                    (navItem.hasDropdown && isDropdownActive(navItem.options))) ? 'active' : ''
                  }
                  onMouseEnter={() => navItem.hasDropdown && handleMouseEnter(index)}
                  onMouseLeave={handleMouseLeave}
                  onClick={(e) => {
                    if (navItem.hasDropdown && window.innerWidth <= MOBILE_BREAKPOINT) {
                      e.stopPropagation();
                      toggleDropdown(index);
                    }
                  }}
                >
                  <NavLink
                    href={!navItem.hasDropdown ? navItem.link : '#'}
                    className={isActive(navItem.link) ? 'active' : ''}
                    target={navItem.redirectNewPage ? "_blank" : "_self"}
                    rel={navItem.redirectNewPage ? "noopener noreferrer" : ""}
                    onClick={(e) => {
                      if (navItem.hasDropdown) {
                        e.preventDefault();
                        if (window.innerWidth <= MOBILE_BREAKPOINT) {
                          toggleDropdown(index);
                        }
                      }
                    }}
                  >
                    {navItem.name}
                  </NavLink>
                  {navItem.hasDropdown && (
                    <DropdownMenu
                      className={activeDropdown === index ? 'active' : ''}
                    >
                      {navItem.options.map((option) => (
                        <DropdownItem 
                          key={option.id}
                          className={isActive(option.link) ? 'active' : ''}
                        >
                          <a 
                            href={option.link}
                            className={isActive(option.link) ? 'active' : ''}
                            target={option.redirectNewPage ? "_blank" : "_self"}
                            rel={option.redirectNewPage ? "noopener noreferrer" : ""}
                          >
                            {option.id}
                          </a>
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  )}
                </NavItem>
              ))}
            </NavList>
          </NavWrapper>
          <MobileMenuIcon onClick={toggleMobileNav}>
            {showMobileNav ? <AiOutlineClose /> : <RiMenu4Fill />}
          </MobileMenuIcon>
        </TopBar>
      </NavContainer>
    </Header>
  );
};

const Header = styled.header`
  width: 100%;
  background-color: #ffffff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 1000;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  position: relative;
`;

const NavContainer = styled.div`
  max-width: 70%;
  margin: 0 auto;
  position: relative;

  @media (max-width: 1024px){
    max-width: 90%;
  }
`;

const LogoWrapper = styled.div`
  cursor: pointer;
  text-decoration: none;
  color: inherit;
  display: block;
`;

const LogoContent = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  
  h3 {
    margin: 0;
  }
  
  img {
    max-height: 50px;
  }

  @media (max-width: 1024px) {
    gap: 5px;
    
    h3 {
      font-size: 1rem;
    }
    
    img {
      max-height: 40px;
    }
  }
`;

const NavList = styled.ul`
  display: flex;
  list-style: none;
  padding: 0;
  margin: 0;
  align-items: center;

  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
  }
`;

const MobileMenuIcon = styled.div`
  display: none;
  
  @media (max-width: 1024px) {
    display: block;
    font-size: 24px;
    cursor: pointer;
    z-index: 1001;
  }
`;

const NavWrapper = styled.nav`
  display: flex;
  justify-content: flex-end;
  flex-grow: 1;

  @media (max-width: 1024px) {
    display: none;
    width: 100vw;
    max-width: 100%;
    background-color: white;
    position: fixed;
    top: var(--header-height, 60px); /* Use dynamically calculated header height */
    left: 0;
    right: 0;
    z-index: 999;
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
    border-top: 1px solid #eee;
    border-bottom: 1px solid #eee;
    overflow-x: auto;
    overflow-y: auto;
    max-height: calc(100vh - var(--header-height, 60px));

    &.mobile-active {
      display: flex;
      flex-direction: column;
    }
  }
`;

const NavItem = styled.li`
  position: relative;
  padding: 15px;

  &.active {
    background-color: rgba(0, 123, 255, 0.05);
  }

  @media (min-width: 1025px) {
    &:hover > ul {
      display: block;
    }
  }

  @media (max-width: 1024px) {
    width: 100%;
    box-sizing: border-box;
    padding: 10px 15px;
    border-bottom: 1px solid #eee;
  }
`;

const NavLink = styled.a`
  text-decoration: none;
  color: #333;
  font-weight: 500;
  transition: color 0.3s ease;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  box-sizing: border-box;

  &:hover {
    color: #CA9D3B;
  }

  &.active {
    color: #CA9D3B;
    font-weight: 600;
  }
`;

const DropdownMenu = styled.ul`
  display: none;
  position: absolute;
  top: 100%;
  left: 0;
  background-color: #fff;
  min-width: 200px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  border-radius: 4px;
  padding: 10px 0;
  list-style: none;
  margin: 0;
  
  &.active {
    display: block;
  }

  @media (max-width: 1024px) {
    position: static;
    box-shadow: none;
    width: 100%;
    display: none;
    padding: 0;
    box-sizing: border-box;

    &.active {
      display: block;
    }
  }
`;

const DropdownItem = styled.li`
  padding: 10px 15px;
  box-sizing: border-box;

  &.active {
    background-color: rgba(0, 123, 255, 0.05);
  }

  @media (max-width: 1024px) {
    border-top: 1px solid #eee;
    background-color: #f9f9f9;
  }

  a {
    text-decoration: none;
    color: #666;
    transition: color 0.3s ease;
    display: block;
    word-break: break-word;

    &:hover {
      color: #CA9D3B;
    }

    &.active {
      color: #CA9D3B;
      font-weight: 600;
    }
  }
`;

export default IUMRSNavBar;